// src/utils/mfa.js
// SMS two-step verification helpers (Firebase Identity Platform multi-factor).
// The heavy lifting is Firebase's; this module keeps the flows in one place
// and the error copy human. NEVER do a pre-auth Firestore read here (the
// 2026-07-14 login-hang rule) — everything runs after Firebase auth events.
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
  multiFactor,
} from "firebase/auth";

export const isMfaEnrolled = (user) =>
  !!user && multiFactor(user).enrolledFactors.length > 0;

// "(555) 201-8890" / "555-201-8890" / "5552018890" -> "+15552018890".
// Already-international numbers ("+44...") pass through. Anything that isn't
// 10 digits (or 11 starting with 1) returns "" so callers can block Send.
export function toE164US(input) {
  const raw = String(input || "").trim();
  if (raw.startsWith("+")) {
    const digits = raw.slice(1).replace(/\D/g, "");
    return digits.length >= 8 ? `+${digits}` : "";
  }
  const d = raw.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return "";
}

// Step 1 of enrollment: text a code to the phone. Returns verificationId.
export async function startEnrollment(auth, user, phoneE164, recaptcha) {
  const session = await multiFactor(user).getSession();
  const provider = new PhoneAuthProvider(auth);
  return provider.verifyPhoneNumber(
    { phoneNumber: phoneE164, session },
    recaptcha,
  );
}

// Step 2 of enrollment: confirm the code, bind the phone to the account.
export async function finishEnrollment(user, verificationId, code) {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(user).enroll(assertion, "Phone");
}

// Login-time challenge, step 1: build the resolver from the sign-in error
// and text a code to the enrolled phone. Returns { resolver, verificationId }.
export async function startMfaSignIn(auth, error, recaptcha) {
  const resolver = getMultiFactorResolver(auth, error);
  const hint = resolver.hints.find(
    (h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
  ) || resolver.hints[0];
  const provider = new PhoneAuthProvider(auth);
  const verificationId = await provider.verifyPhoneNumber(
    { multiFactorHint: hint, session: resolver.session },
    recaptcha,
  );
  return { resolver, verificationId };
}

// Login-time challenge, step 2: complete sign-in with the texted code.
export async function finishMfaSignIn(resolver, verificationId, code) {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  return resolver.resolveSignIn(assertion);
}

// Plain-English error copy for the enrollment/challenge screens.
export function mfaErrorMessage(err) {
  const code = err?.code || "";
  if (code === "auth/invalid-verification-code")
    return "That code did not match. Check the text and try again.";
  if (code === "auth/code-expired")
    return "That code expired. Send a new one and try again.";
  if (code === "auth/too-many-requests")
    return "Too many tries. Wait a few minutes, then try again.";
  if (code === "auth/requires-recent-login")
    return "For security, please log out and log back in, then add your phone right away.";
  if (code === "auth/invalid-phone-number")
    return "That phone number does not look right. Use your 10 digit cell number.";
  if (code === "auth/operation-not-allowed")
    return "Two-step verification is not switched on for this system yet. Tell Henry.";
  return "Something went wrong. Try again, and tell Henry if it keeps happening.";
}
